import { Box, Typography } from "@analyticsMD/quil";
import ErrorOutlineIcon from "@mui/icons-material/Error";
import { css } from "@emotion/css";
import { common, grey, red, blue, purple } from "@mui/material/colors";
import { PURPLE_TINT } from "./malnutrition-colors";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC } from "react";
import type {
  MalnutritionCriterion,
  MalnutritionCriterionMetric,
  MalnutritionCriterionQuote,
  MalnutritionStatus,
} from "../../types/Malnutrition";

const FLAGGED_RED = red[800];
const PROTOCOL_BLUE = blue[900];

type Variant = "flagged" | "protocol";

interface VariantTheme {
  accent: string;
  headerLabel: string;
  summaryLabel: (n: number) => string;
}

const VARIANTS: Record<Variant, VariantTheme> = {
  flagged: {
    accent: FLAGGED_RED,
    headerLabel: "WHY FLAGGED",
    summaryLabel: (n) => `· ${n} criteria met`,
  },
  protocol: {
    accent: PROTOCOL_BLUE,
    headerLabel: "PROTOCOL TRIGGER",
    summaryLabel: (n) => `· ${n} rules met`,
  },
};

const styles = {
  card: (accent: string) => css`
    background: ${common.white};
    border-left: 3px solid ${accent};
    border-radius: 0.375rem;
    padding: 0.75rem 0.875rem;
    margin-bottom: 0.625rem;
  `,
  quoteBlock: css`
    background: ${PURPLE_TINT};
    border-left: 3px solid ${purple[300]};
    padding: 0.625rem 0.75rem;
    margin: 0.5rem 0 0.625rem 0;
    font-style: italic;
    font-size: 0.875rem;
    color: ${grey[800]};
    border-radius: 0.125rem;
  `,
  source: css`
    color: ${grey[700]};
    font-size: 0.75rem;
  `,
  metricRow: css`
    display: flex;
    gap: 0.375rem;
    font-size: 0.85rem;
    line-height: 1.45;
  `,
};

const cardSxStyles = {
  title: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: grey[800],
    marginBottom: "0.375rem",
  },
};

const metricSx = (accent: string) => ({
  label: {
    fontSize: "0.85rem",
    color: accent,
    fontWeight: 600,
  },
  value: {
    fontSize: "0.85rem",
    color: accent,
  },
});

const headingSx = (accent: string) => ({
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: accent,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  display: "flex",
  alignItems: "center",
});

const iconSx = (accent: string) => ({
  backgroundColor: accent,
  color: common.white,
  borderRadius: "0.25rem",
  padding: "0.125rem",
  fontSize: "1rem",
  marginRight: "0.5rem",
});

const MetricCriterionCard: FC<{
  criterion: MalnutritionCriterionMetric;
  accent: string;
}> = ({ criterion, accent }) => {
  const sx = metricSx(accent);
  return (
    <Box className={styles.card(accent)}>
      <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
      {criterion.rows.map((row, idx) => (
        <Box key={idx} className={styles.metricRow}>
          {row.label && (
            <Box component="span" sx={sx.label}>
              {row.label}:
            </Box>
          )}
          <Box component="span" sx={sx.value}>
            {row.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const QuoteCriterionCard: FC<{
  criterion: MalnutritionCriterionQuote;
  accent: string;
}> = ({ criterion, accent }) => (
  <Box className={styles.card(accent)}>
    <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
    <Box className={styles.quoteBlock}>&ldquo;{criterion.quote}&rdquo;</Box>
    <Box className={styles.source}>
      {[
        criterion.source_kind,
        criterion.source_author,
        criterion.source_timestamp,
      ]
        .filter(Boolean)
        .join(" · ")}
    </Box>
  </Box>
);

export const CriterionCard: FC<{
  criterion: MalnutritionCriterion;
  variant?: Variant;
}> = ({ criterion, variant = "flagged" }) => {
  const accent = VARIANTS[variant].accent;
  if (criterion.kind === "metric") {
    return <MetricCriterionCard criterion={criterion} accent={accent} />;
  }
  return <QuoteCriterionCard criterion={criterion} accent={accent} />;
};

const CriteriaSection: FC<{
  variant: Variant;
  criteria: MalnutritionCriterion[];
}> = ({ variant, criteria }) => {
  const theme = VARIANTS[variant];
  return (
    <CollapsibleSection
      defaultOpen
      title={theme.headerLabel}
      icon={<ErrorOutlineIcon sx={iconSx(theme.accent)} />}
      summary={<>{theme.summaryLabel(criteria.length)}</>}
      headingTextSx={headingSx(theme.accent)}
      outerClassName={css`
        background: ${grey[50]};
        border-radius: 0.5rem;
        margin: 0 0.75rem 0.75rem;
        padding: 0.25rem 1rem 0.25rem 1rem;
      `}
      dataTestId={
        variant === "protocol"
          ? "protocol-trigger-section"
          : "why-flagged-section"
      }
    >
      {criteria.map((criterion, idx) => (
        <CriterionCard key={idx} criterion={criterion} variant={variant} />
      ))}
    </CollapsibleSection>
  );
};

interface WhyFlaggedSectionProps {
  criteria: MalnutritionCriterion[];
  status: MalnutritionStatus;
}

// Criteria split by source: model -> "Why Flagged", protocol -> "Protocol
// Trigger", each shown when present. Exception: a "protocol" status shows only
// the Protocol Trigger section (Why Flagged is suppressed even if there are
// model criteria). (A blank status hides everything — handled by the caller.)
const WhyFlaggedSection: FC<WhyFlaggedSectionProps> = ({ criteria, status }) => {
  const modelCriteria = criteria.filter((c) => c.source === "model");
  const protocolCriteria = criteria.filter((c) => c.source === "protocol");
  const showModel = status !== "protocol" && modelCriteria.length > 0;
  return (
    <>
      {showModel && (
        <CriteriaSection variant="flagged" criteria={modelCriteria} />
      )}
      {protocolCriteria.length > 0 && (
        <CriteriaSection variant="protocol" criteria={protocolCriteria} />
      )}
    </>
  );
};

export default WhyFlaggedSection;
